/**
 * Represents a reference to a menu item within a menu or menu group.
 *
 * This is the SOURCE OF TRUTH for the item-to-menu-group relationship.
 * Menu groups own the relationship via their items[] array.
 *
 * Supports:
 * - Item ordering within the menu group (position)
 * - Required items for combos/deals (isRequired)
 * - Menu-specific price overrides (priceOverride)
 * - Visibility override per menu group (isHidden)
 */
export interface IItemReference {
  /** The ID of the referenced item */
  readonly itemId: number;
  /** Display position within this menu group (0-based) */
  readonly position: number;
  /** Whether this item is required (for combos/meal deals) */
  readonly isRequired: boolean;
  /** Price override for this item in this specific menu group (optional) */
  readonly priceOverride?: number;
  /** Hide this item in this specific menu group (optional) */
  readonly isHidden?: boolean;
  /** Optional display name override for this menu group context */
  readonly displayNameOverride?: string;
}

export type IItemReferenceData = Partial<IItemReference>;

export class ItemReference implements IItemReference {
  readonly itemId: number;
  readonly position: number;
  readonly isRequired: boolean;
  readonly priceOverride?: number;
  readonly isHidden?: boolean;
  readonly displayNameOverride?: string;

  constructor(data: IItemReferenceData) {
    this.itemId = data.itemId!;
    this.position = data.position ?? 0;
    this.isRequired = data.isRequired ?? false;
    this.priceOverride = data.priceOverride;
    this.isHidden = data.isHidden;
    this.displayNameOverride = data.displayNameOverride;
  }

  toJSON(): IItemReference {
    const result: IItemReference = {
      itemId: this.itemId,
      position: this.position,
      isRequired: this.isRequired,
    };

    // Only include optional fields if they have values
    if (this.priceOverride !== undefined) {
      (result as any).priceOverride = this.priceOverride;
    }
    if (this.isHidden !== undefined) {
      (result as any).isHidden = this.isHidden;
    }
    if (this.displayNameOverride !== undefined) {
      (result as any).displayNameOverride = this.displayNameOverride;
    }

    return result;
  }

  static fromJSON(data: Record<string, unknown>): ItemReference {
    if (!data.itemId) throw new Error("ItemReference.fromJSON: itemId is missing.");
    // Provide default position of 0 if missing (for backward compatibility with existing data)
    const position = typeof data.position === 'number' ? data.position : 0;

    return new ItemReference({
      itemId: data.itemId as number,
      position,
      isRequired: data.isRequired as boolean | undefined,
      priceOverride: data.priceOverride as number | undefined,
      isHidden: data.isHidden as boolean | undefined,
      displayNameOverride: data.displayNameOverride as string | undefined,
    });
  }

  /**
   * Get the effective price for an item in this menu group context.
   * Returns the override price if set, otherwise returns undefined
   * (caller should use the item's base price).
   */
  getEffectivePrice(itemBasePrice?: number): number | undefined {
    return this.priceOverride ?? itemBasePrice;
  }

  /**
   * Check if this item is visible in this menu group context.
   */
  isVisible(): boolean {
    return !this.isHidden;
  }
}
